import { Injectable, inject, signal } from '@angular/core'
import { StateService } from './state.service'
import {
    LocationPermissionStatus,
    LocationService
} from './location.service';

@Injectable({
    providedIn: 'root'
})
export class DeviceService {
    private readonly locationService = inject(LocationService);
    readonly locationState = this.locationService.state;

    async requestLocation() {
        return await this.locationService.requestLocation();
    }

    #stateService = inject(StateService)
    device = signal<Device>(null);

    async initDevice() {
        const device = this.#stateService.Device ?? this.#constructDevice()
        const result = await this.locationService.initialize();
        if (result.status === LocationPermissionStatus.Granted) {
            device.latitude = result.location?.latitude.toString() || '';
            device.longitude = result.location?.longitude.toString() || '';
            device.geoAccuracy = result.location?.accuracy;
        }
        this.#stateService.Device = device;
        this.device.set(device);
    }

    #constructDevice(): Device {
        const userAgent = navigator.userAgent

        const platformInfo = this.getPlatformInfo()
        const deviceType = this.getDeviceType()
        const browserInfo = this.getBrowserInfo()
        const browserIsMobile = deviceType === 'Mobile' || deviceType === 'Tablet'
        const browserMobileModel = browserIsMobile ? this.getMobileModel() : ''
        return {
            platform: platformInfo,
            deviceType: deviceType,
            browserName: browserInfo.name,
            userAgent,
            browserIsMobile,
            browserMobileModel,
            geoAccuracy: undefined,
            latitude: undefined,
            longitude: undefined
        }
    }

    getGeoCoordinates(): Promise<GeolocationPosition> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Browser geolocation is not supported.'))
                return
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve(position)
                },
                (error) => {
                    reject(error)
                },
                { enableHighAccuracy: false, maximumAge: 0, timeout: 15000 }
            )
        })
    }

    getPlatformInfo() {
        const ua = navigator.userAgent
        if (/Macintosh/.test(ua)) return 'Apple'
        if (/Windows/.test(ua)) return 'Windows'
        if (/Android/.test(ua)) return 'Android'
        if (/Linux/.test(ua)) return 'Linux'
        if (/iPhone|iPad|iPod/.test(ua)) return 'Apple'
        return 'Unknown'
    }

    getDeviceType(): 'Tablet' | 'Mobile' | 'Web' {
        const ua = navigator.userAgent
        if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
            return 'Tablet'
        }
        if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(ua)) {
            return 'Mobile'
        }
        return 'Web'
    }

    getMobileModel() {
        const userAgent = navigator.userAgent
        const modelRegex = /(Galaxy|iPhone|Pixel|tablet|ipad|playbook|silk|Mate|P|Find)/i;

        const modelMatch = userAgent.match(modelRegex);

        if (modelMatch) {
            return `${modelMatch[1]}`;
        }
        return ''
    }

    getBrowserInfo() {
        const ua = navigator.userAgent
        let tem
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || []
            return { name: 'IE', version: (tem[1] || '') }
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge)\/(\d+)/)
            if (tem != null) return { name: tem[1], version: tem[2] }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?']
        if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1])
        return {
            name: M[0],
            version: M[1]
        }
    }
}

export interface Device {
    platform: string
    deviceType: string
    browserName: string
    browserIsMobile: boolean
    browserMobileModel: string
    latitude?: string
    longitude?: string
    userAgent: string
    geoAccuracy?: number
}

export interface GeoLocation {
    countryCode: string
    ipAddress: string
}

export enum Message {
    Success = "Success",
    Error = "Error",
    Warning = "Warning",
    Info = "Info",
    ErrorMessage = "An error occurred. Please try again later.",
    ErrorMessageLocationAccess = "Location access blocked. Please enable to continue",
    ErrorMessageCountryAccess = "Access blocked. Application is not available in your country.",
    ErrorMessageDeviceId = "Error in getting deviceId. Please try again later.",
    ErrorMessageDeviceType = "Error in getting device type. Please try again later.",
    ErrorMessageDeviceIpAddress = "Error in getting device IP address. Please try again later.",
    ErrorMessageDeviceGeoCoordinates = "Error in getting device geo coordinates. Please try again later.",
    ErrorMessageDeviceHashId = "Error in getting device hash id. Please try again later.",
    ErrorMessageDevicermation = "Error in getting device information. Please try again later.",
    ErrorMessageGetToken = "Error in getting token. Please try again later.",
    ErrorMessageSession = "Error in starting the session. Please try again later.",
    ErrorMessageTokenDate = "Error in getting token date. Please try again later.",
    ErrorMessageGetKeys = "Error in getting keys. Please try again later.",
    ErrorMessageExpiredLink = "Link has been expired",
    LogoutMessage = "You have been logged out.",
    InvalidCredentials = "Invalid username or password.",
    EncryptedTextCopiedToClipboard = "Encrypted text copied to clipboard.",
    DecryptedTextCopiedToClipboard = "Decrypted text copied to clipboard.",
    EncryptedTextPastedFromClipboard = "Encrypted text pasted from clipboard.",
    DecryptedTextPastedFromClipboard = "Decrypted text pasted from clipboard.",
    EncryptedDataCleared = "Encrypted data cleared.",
    DecryptedDataCleared = "Decrypted data cleared.",
    Encryption = "encryption",
    ErrorMessageLoginFailed = "Incorrect email/password",
    ErrorMessageIncorrectLoginCredentials = "Incorrect email or password. Please verify and try again.",
    PinResentSuccess = "Pin resent successfully.",
    Forbidden = "Your Session has been expired",
    NotAuthporized = "Not Authorized",
}

export const serviceDownMessages = [
    {
        severity: 'warn',
        detail: 'Provider Services Down. Only Lead Data will be collected',
    }
]
